import React, {useContext} from 'react'
import {Box, ResponsiveValue} from '@miro-site/core'
import styled from 'styled-components'

const calculateGutterCompensation = (gutter: ResponsiveValue<number | null>) => {
  const toGutterCompensation = (g: number | null) => {
    if (g === null) {
      return null
    }

    return -1 * (g / 2)
  }

  if (gutter === null) {
    return null
  }

  if (!Array.isArray(gutter)) {
    return toGutterCompensation(gutter)
  } else {
    return gutter.map(toGutterCompensation)
  }
}

const calculateHalfGutterWidth = (gutter: ResponsiveValue<number | null>) => {
  const toHalfGutter = (g: number | null) => {
    if (g === null) {
      return null
    }

    return g / 2
  }

  if (gutter === null) {
    return null
  }

  if (!Array.isArray(gutter)) {
    return toHalfGutter(gutter)
  } else {
    return gutter.map(toHalfGutter)
  }
}

const toWidth = (columnCount: number | null, totalColumns: number | null) => {
  if (columnCount === null || totalColumns === null) {
    return null
  }

  return `${(Math.min(totalColumns, columnCount) / totalColumns) * 100}%`
}

const calculateColumnWidth = (
  gridColumns: ResponsiveValue<number | null>,
  columns: ResponsiveValue<number | null>,
): ResponsiveValue<string | null> => {
  if (gridColumns === null || columns === null) {
    return null
  }

  if (!Array.isArray(gridColumns) && !Array.isArray(columns)) {
    return toWidth(columns, gridColumns)
  }

  const gridColumnsArray = Array.isArray(gridColumns) ? gridColumns : [gridColumns]
  const columnsArray = Array.isArray(columns) ? columns : [columns]

  return [...Array(Math.max(gridColumnsArray.length, columnsArray.length))].map((v, i) => {
    const columns =
      typeof columnsArray[i] === 'undefined'
        ? columnsArray[columnsArray.length - 1]
        : columnsArray[i]
    const gridColumns =
      typeof gridColumnsArray[i] === 'undefined'
        ? gridColumnsArray[gridColumnsArray.length - 1]
        : gridColumnsArray[i]

    return toWidth(columns, gridColumns)
  })
}

const GridContext = React.createContext<{
  columns: ResponsiveValue<number | null>
  gutter: ResponsiveValue<number | null>
}>({
  columns: null,
  gutter: null,
})

type GridAlign = {
  align?: 'top' | 'bottom' | 'stretch' | 'center'
}

const ALIGN_MAP = {
  top: 'flex-start',
  bottom: 'flex-end',
  stretch: 'stretch',
  center: 'center',
}

type RowType = {
  $gutterCompensation: ResponsiveValue<number | null>
  as?: React.ElementType
} & GridAlign

const Row = styled(Box).attrs<RowType>(({$gutterCompensation, align = 'top', as = 'div'}) => ({
  as,
  $mx: $gutterCompensation,
  $styles: {
    display: 'flex',
    flex: '0 1 auto',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: ALIGN_MAP[align],
  },
}))<RowType>``

interface ColumnProps {
  as?: React.ElementType
  columns?: Array<number | null>
  children: React.ReactNode
}

const Column = (props: ColumnProps): React.ReactElement => {
  const {columns, children, ...restProps} = props

  const gridSettings = useContext(GridContext)

  const halfGutterWidth = calculateHalfGutterWidth(gridSettings.gutter)

  const width = calculateColumnWidth(gridSettings.columns, columns || gridSettings.columns)

  return (
    <Box
      $px={halfGutterWidth}
      $styles={{
        flex: '0 0 auto',
        flexBasis: width,
        maxWidth: width,
      }}
      {...restProps}
    >
      {children}
    </Box>
  )
}

interface GridProps extends GridAlign {
  as?: React.ElementType
  columns: ResponsiveValue<number | null>
  gutter: ResponsiveValue<number | null>
  children: React.ReactNode
}

const Grid = (props: GridProps): React.ReactElement => {
  const {children, columns, gutter, ...restProps} = props

  const gutterCompensation = calculateGutterCompensation(gutter)

  return (
    <GridContext.Provider value={{columns, gutter}}>
      <Row $gutterCompensation={gutterCompensation} {...restProps}>
        {children}
      </Row>
    </GridContext.Provider>
  )
}

export default Object.assign(Grid, {Column: Column})
